import axios from 'axios';
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import { useNavigate } from "react-router-dom";
import shuffleArray from "../util/shuffleArray";
import { useAuth } from './../AuthContext';
import careerRecommendations from "./careerRecommendations";
import categories from "./categories";
import generateYesNoQuestions from "./generateYesNoQuestions";

// const BASE_URL = "http://localhost:8000";
const BASE_URL = "https://idealpursuits.co.in/api";

const maxQuestionsPerType = 10;

const SelfPacedLearning = ({ isOpen, closeModel, reloadFunction }) => {
    const { userData, isLogged, login } = useAuth();
    const navigate = useNavigate();
    const [introShown, setIntroShown] = useState(true);
    const [yesNoQuestions, setYesNoQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [questionType, setQuestionType] = useState("yesNo");
    const [categoryScores, setCategoryScores] = useState({});
    const [recommendedCareers, setRecommendedCareers] = useState(null);
    const [currentCheckboxOptions, setCurrentCheckboxOptions] = useState([]);
    const [showAppreciation, setShowAppreciation] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    useEffect(() => {
        const generatedYesNoQuestions = shuffleArray(generateYesNoQuestions()).slice(0, maxQuestionsPerType);
        setYesNoQuestions(generatedYesNoQuestions);
        setCurrentCheckboxOptions(shuffleArray(Object.values(categories).flat()).slice(0, 6));
    }, []);

    const handleYesNoResponse = (isYes, categories) => {
        if (isYes) {
            const updatedScores = { ...categoryScores };
            categories.forEach((category) => {
                updatedScores[category] = (updatedScores[category] || 0) + 1;
            });
            setCategoryScores(updatedScores);
        }

        if (currentQuestionIndex < yesNoQuestions.length - 1) {
            setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
        } else {
            setShowAppreciation(true);
        }
    };

    let shownPhrasesSet = new Set(); // Global variable to track shown phrases

    const handleCheckboxSubmit = (selectedPhrases) => {
        const updatedScores = { ...categoryScores };

        // Add scores for selected phrases
        selectedPhrases.forEach((phrase) => {
            const category = Object.keys(categories).find((key) =>
                categories[key].includes(phrase)
            );
            if (category) {
                updatedScores[category] = (updatedScores[category] || 0) + 1;
            }
        });

        setCategoryScores(updatedScores);

        // Track shown phrases
        shownPhrasesSet = new Set([...shownPhrasesSet, ...currentCheckboxOptions]);

        // Check for threshold score or decide next set of options
        const topCategory = Object.entries(updatedScores).find(([_, score]) => score >= 6);

        if (topCategory) {
            recommendCareers(updatedScores);
        } else {
            // Compute next categories, excluding already shown phrases
            const nextCategories =
                selectedPhrases.length > 0
                    ? selectedPhrases.flatMap((phrase) => {
                        const category = Object.keys(categories).find((key) =>
                            categories[key].includes(phrase)
                        );
                        return categories[category] || [];
                    })
                    : Object.values(categories).flat(); // Fallback to mixed categories if skipped

            // Exclude shown phrases and shuffle the remaining options
            let filteredPhrases = nextCategories.filter(
                (phrase) => !shownPhrasesSet.has(phrase)
            );

            // If not enough phrases, fallback to a mixed set excluding shown phrases
            if (filteredPhrases.length < 2) {
                filteredPhrases = Object.values(categories)
                    .flat()
                    .filter((phrase) => !shownPhrasesSet.has(phrase));
            }

            // Shuffle and select unique phrases
            const uniqueOptions = shuffleArray(filteredPhrases).slice(0, 6);

            // Ensure unique options in current set
            setCurrentCheckboxOptions(shuffleArray([...new Set(uniqueOptions)]).slice(0, 6));
        }
    };

    const recommendCareers = (scores) => {
        // Get top categories with scores 5 or 6
        const topCategories = Object.entries(scores)
            .filter(([_, score]) => score >= 5) // Include scores >= 5
            .sort((a, b) => b[1] - a[1]);

        const careers = topCategories.flatMap(([category]) =>
            careerRecommendations[category]?.careers || []
        );

        const descriptions = topCategories.map(
            ([category]) =>
                careerRecommendations[category]?.description || "Interesting career potential."
        );

        const topCategoryNames = topCategories.map(([cat]) => cat);

        // Set recommendations
        setRecommendedCareers({
            topCategories: topCategoryNames,
            careers: [...new Set(careers)],
            descriptions,
        });

        // Store recommended career and time in the database
        saveRecommendationToDatabase(topCategoryNames, userData.userId);

        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
    };

    const saveRecommendationToDatabase = async (topCategories, userId) => {
        const data = {
            categories: topCategories.join(", "), // Combine categories into a single string
            recommendedTime: Date.now(), // Use current milliseconds timestamp
            userId: userId, // Pass the user ID
        };

        try {
            await axios.post(`${BASE_URL}/setMeetingNotAttended.php`, {
                userId: userData.userId
            });

            await axios.post(`${BASE_URL}/setExpertMeetingNotAttended.php`, {
                userId: userData.userId
            });

            const response = await axios.post(`${BASE_URL}/saveProgress.php`, data);
            if (response.data.success) {
                // Update userData in authContext
                const updatedUserData = {
                    ...userData,
                    courseTaken: false,
                    InternshipTaken: false,
                    InternshipTakenTime: false,
                    spfFinished: false,
                    recommendedCareer: data.categories,
                    recommendedCareerTime: data.recommendedTime,
                };
                login(updatedUserData);
                
            } else {
                console.error("Failed to save recommendation:", response.data.error);
            }
        } catch (error) {
            console.error("Error saving recommendation:", error);
        }
    };

    const viewCourses = (category) => {
        const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(category)}+Courses`;
        window.open(googleSearchUrl, "_blank");
    };

    // const viewOnYouTube = (categories) => {
    //     const searchQuery = categories.join(" career advice");
    //     const youtubeUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
    //     window.open(youtubeUrl, "_blank");
    // };

    const retakeQuiz = () => {
        setIntroShown(true);
        setCurrentQuestionIndex(0);
        setQuestionType("yesNo");
        const generatedYesNoQuestions = shuffleArray(generateYesNoQuestions()).slice(0, maxQuestionsPerType);
        setYesNoQuestions(generatedYesNoQuestions);
        setCategoryScores({});
        setRecommendedCareers(null);
        setShowAppreciation(false);
        setCurrentCheckboxOptions(shuffleArray(Object.values(categories).flat()).slice(0, 6));
    };

    if (!isOpen) return null;

    const animationVariants = {
        initial: { opacity: 0, y: 30 },
        animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
        exit: { opacity: 0, y: -30, transition: { duration: 0.5 } },
    };

    if (!isLogged) {
        return (
            <div className="booking-popup-modal">
                <div className="popup-content">
                    <h2 style={{ color: "black" }}>Kindly, Login to explore yourself</h2>
                    <p style={{ color: "black" }}>
                        This self-paced learning journey help to explore your interests and choose the career that aligns with your passion.
                    </p>
                    <button className="agree-button" onClick={() => navigate('/login')}>
                        Login
                    </button>
                </div>
            </div>
        );
    }

    if (introShown) {
        return (
            <motion.div className="quiz-modal" {...animationVariants}>
                <div className="quiz-modal-content">
                    <h2 style={{ fontStyle: "italic" }}>Explore Your Career!</h2>
                    <p>
                        This self-paced learning experience helps you discover your ideal career path based on
                        your preferences, skills, and interests. The process includes:
                    </p>
                    <ul
                        style={{
                            textAlign: "left",
                            fontWeight: "300",
                            fontStyle: "italic",
                            fontSize: "16px",
                        }}
                    >
                        <li>Answering a few Yes/No questions to determine your broad interests.</li>
                        <li>Selecting phrases from dynamically changing options for deeper insights.</li>
                        <li>Receiving career recommendations tailored just for you.</li>
                    </ul>
                    <button className="start-button" onClick={() => setIntroShown(false)}>
                        Find My Career <ArrowRight size={10} />
                    </button>
                </div>
            </motion.div>
        );
    }

    if (showAppreciation) {
        return (
            <motion.div className="quiz-modal" {...animationVariants}>
                <div className="quiz-modal-content">
                    <h2>Great Job!</h2>
                    <p>
                        You've completed the first stage of the questionnaire. Up next, you'll dive deeper by
                        selecting phrases that resonate with your interests.
                    </p>
                    <button
                        className="next-button"
                        onClick={() => {
                            setShowAppreciation(false);
                            setQuestionType("checkbox");
                        }}
                    >
                        Continue
                    </button>
                </div>
            </motion.div>
        );
    }

    if (recommendedCareers) {
        if(reloadFunction){
            reloadFunction();
        }
        return (
            <motion.div className="quiz-modal" {...animationVariants}>
                {showConfetti && <Confetti />}
                <div className="quiz-modal-content">
                    <button className="quiz-close-button" onClick={closeModel}>
                        &times;
                    </button>
                    <div style={{ fontStyle: "italic" }} className="recommendation-section">
                        <h3>Your Career Recommendations:</h3>
                        {recommendedCareers.topCategories.map((category, index) => (
                            <div key={category}>
                                <h3 style={{ color: "green" }}>
                                    "{category.charAt(0).toUpperCase() + category.slice(1)}"
                                </h3>
                                <p>{recommendedCareers.descriptions[index]}</p>
                            </div>
                        ))}
                        <h3>Potential Career Opportunities:</h3>
                        <p>Explore these careers, one by one, read articles and learn about them.</p>
                        <ul>
                            {recommendedCareers.careers.map((career) => (
                                <li key={career}>
                                    <a
                                        href={`https://www.google.com/search?q=${career}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {career}
                                    </a>
                                </li>
                            ))}
                        </ul>
                        <div className="career-roadmap">
                            <h4>📍 Your Career Roadmap</h4>
                            <div className="roadmap-steps">
                                <span className="step completed">Career Test ✅</span>
                                <span className="arrow">→</span>
                                <span className="step">Courses</span>
                                <span className="arrow">→</span>
                                <span className="step">Internship</span>
                                <span className="arrow">→</span>
                                <span className="meet-step">Coach</span>
                                <span className="arrow">→</span>
                                <span className="meet-step">Expert</span>
                            </div>
                        </div>
                        <div className="action-buttons">
                            {recommendedCareers.topCategories.map((category, index) => (
                                <button
                                    key={index}
                                    className="view-internships-button"
                                    onClick={() => {
                                        viewCourses(category);
                                        closeModel();
                                    }}
                                >
                                    View Courses for {category}
                                </button>
                            ))}
                            <button className="retake-quiz-button" onClick={retakeQuiz}>
                                Retake Quiz
                            </button>
                            <p>
                                You can track your progress in your <a href='/profile'>profile</a> anytime.
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <AnimatePresence>
            <motion.div className="quiz-modal" {...animationVariants}>
                <div className="quiz-modal-content">
                    <button className="quiz-close-button" onClick={closeModel}>
                        &times;
                    </button>
                    {questionType === "yesNo" ? (
                        <>
                            <div className="progress-bar">
                                <div
                                    className="progress-bar-fill"
                                    style={{
                                        width: `${((currentQuestionIndex + 1) / yesNoQuestions.length) * 100}%`,
                                    }}
                                />
                            </div>
                            <h3>Career Exploration Arena</h3>
                            <motion.p>{yesNoQuestions[currentQuestionIndex].text}</motion.p>
                            <div className="response-buttons">
                                <button
                                    className="response-button"
                                    onClick={() =>
                                        handleYesNoResponse(true, yesNoQuestions[currentQuestionIndex].categories)
                                    }
                                >
                                    Yes
                                </button>
                                <button
                                    className="response-button"
                                    onClick={() => handleYesNoResponse(false, [])}
                                >
                                    No
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h3>Select the phrases that resonate with you:</h3>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();

                                    // Retrieve selected phrases
                                    const selectedPhrases = Array.from(
                                        e.target.querySelectorAll("input[type=checkbox]:checked")
                                    ).map((checkbox) => checkbox.value);

                                    // Pass selected phrases to the handler
                                    handleCheckboxSubmit(selectedPhrases);

                                    // Reset checkboxes for the next set of options
                                    setCurrentCheckboxOptions((prevOptions) =>
                                        prevOptions.filter((option) => !selectedPhrases.includes(option))
                                    );

                                    // Clear form
                                    e.target.reset();
                                }}
                            >
                                <div className="checkbox-group">
                                    {currentCheckboxOptions.map((option, index) => (
                                        <label key={index}>
                                            <input type="checkbox" value={option} />
                                            {option}
                                        </label>
                                    ))}
                                </div>
                                <div style={{ width: "100%", display: "flex", justifyContent: "space-around", marginTop: "10px" }}>
                                    <button type="submit">Skip</button>
                                    <button style={{ backgroundColor: "blue", color: "white" }} type="submit">Next</button></div>
                            </form>
                        </>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SelfPacedLearning;
